import { useState, useMemo } from "react";
import clsx from "clsx";
import { linkifyHtml } from "@/utils/linkify";
import { NewsFeedItem } from "@/shared/api/firehose/models";
import { Button } from "@/ui-components/button-with-loading";
import { useHeadlineFormStore } from "@/widget-components/create-headline/models/store";
import { useHasInternalNewsquawkUserRole } from "@/hooks/useHasInternalNewsquawkUserRole";
import { BackendSource, backendSources, sourceLabels } from "@/shared/api/firehose";
import { publicLabelSources } from "../../shared/hooks/useFirehoseSources";
import { normalizeTickers } from "../utils";
import { HighlightInfo } from "@/utils/portfolioUtils";
import { highlightTermsInText } from "@/utils/text-highlight";
import { safeClipboardWrite } from "@/utils/clipboard";

const defaultBadgeStyle = "bg-stone-300/20 border-stone-300/30";

const defaultStyles = backendSources.reduce(
  (acc, source) => {
    acc[source] = defaultBadgeStyle;
    return acc;
  },
  {} as Record<BackendSource, string>
);

const badgeStyles: Record<BackendSource | "other", string> = {
  ...defaultStyles,

  "stocks-one": "bg-squawk-blue/20 border-squawk-blue/30",
  "stocks-two": "bg-indigo-400/20 border-indigo-400/30",
  "stocks-three": "bg-rose-400/20 border-rose-400/30",
  "crypto-web-news": "bg-amber-400/20 border-amber-400/30",
  "energy-web-news": "bg-lime-300/20 border-lime-300/30",
  "energy-x": "bg-teal-300/20 border-teal-300/30",
  "truth-social": "bg-pink-400/20 border-pink-400/30",
  "fin-x": "bg-fuchsia-400/20 border-fuchsia-400/30",
  other: defaultBadgeStyle,
};

type FirehoseItemDetailsProps = NewsFeedItem & {
  highlightInfo?: HighlightInfo;
};

export function FirehoseItemDetails(props: FirehoseItemDetailsProps) {
  const { setHeadline } = useHeadlineFormStore();
  const internalUser = useHasInternalNewsquawkUserRole();
  const isXSource = props.backend_source === "fin-x" || props.backend_source === "energy-x";
  const hideXLink = isXSource && !internalUser;

  // Helper to apply highlighting to text content
  const applyHighlight = (text: string | null | undefined): string => {
    if (!text || !props.highlightInfo?.matchedTickers?.length) return text || "";
    return highlightTermsInText(text, props.highlightInfo.matchedTickers, props.highlightInfo.color);
  };

  // Show real supplier names for stocks sources to external users
  const displaySource = useMemo(() => {
    if (internalUser) return props.source;
    if (publicLabelSources.includes(props.backend_source)) {
      return sourceLabels[props.backend_source];
    }
    return props.source;
  }, [internalUser, props.source, props.backend_source]);

  const handlePromoteClick = () => {
    // clearHeadline();

    setHeadline({
      subject: props.title,
      source: props.source_attributable || "",
      content: props.body || props.title,
      linked_content: props.url,
    });
  };

  const tickers =
    "tickers" in props && props.tickers ? normalizeTickers(props.tickers) : [];

  const [copyStatus, setCopyStatus] = useState("Copy Details");

  const handleCopyDetails = () => {
    const text = `
Title: ${props.title}
Source: Newsquawk - ${displaySource}
${tickers.length ? `Tickers: ${tickers.join(", ")}\n` : ""}Created: ${new Date(props.created + "Z").toLocaleString()}
${!hideXLink && props.url ? `URL: ${props.url}\n\n` : ""}
${props.body || ""}
    `.trim();

    safeClipboardWrite(text).then((ok) => {
      if (ok) {
        setCopyStatus("Copied!");
        setTimeout(() => setCopyStatus("Copy Details"), 2000);
      }
    });
  };

  return (
    <>
      <div className="bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-xs space-y-1 p-4 transition-colors border-x border-b border-squawk-blue/40">
        <div className="prose dark:prose-invert prose-sm text-xs max-w-none">
          <h2 className="font-medium text-xs text-gray-700 dark:text-gray-300">
            <span
              className={clsx(
                "iniline-block capitalize text-gray-800 dark:text-gray-200 rounded px-1 mr-1 border font-normal",
                // @ts-ignore
                badgeStyles[props.backend_source] ?? badgeStyles["other"]
              )}
            >
              {displaySource}
            </span>
            {/* Title + inline tickers (if any) */}
            <span dangerouslySetInnerHTML={{ __html: linkifyHtml(applyHighlight(props.title)) }} />{" "}
            {tickers.length > 0 && (
              <span className="uppercase">({tickers.join(", ")})</span>
            )}
          </h2>
          <section
            className="text-xs text-gray-500 dark:text-gray-400"
            dangerouslySetInnerHTML={{
              __html: linkifyHtml(applyHighlight(props.body)),
            }}
          />

          {!hideXLink && props.url && (
            <p className="text-green">
              <a target="_blank" href={props.url} rel="noreferrer">
                {props.url}
              </a>
            </p>
          )}
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="secondary" size="md" onClick={handleCopyDetails}>
            {copyStatus}
          </Button>
          {internalUser && (
            <div className="flex justify-end">
              <Button
                variant="secondary"
                size="md"
                onClick={handlePromoteClick}
              >
                Promote to Headlines
              </Button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}